import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { sortBySortOrder } from '@/utils/sort';
import {
  surveyDetailQueryOptions,
  surveySubmissionsInfiniteQueryOptions,
} from '../api/surveys.api';
import type { SurveySubmissionListParams } from '../types/survey.types';
import {
  getSurveySubmissionColumns,
  SURVEY_SUBMISSION_NAME_COLUMN_ID,
} from './SurveySubmissionColumns';

interface SurveySubmissionListTableProps {
  surveyId: number;
  listQueryParams: Omit<SurveySubmissionListParams, 'page'>;
  searchText: string | undefined;
}

export function SurveySubmissionListTable({
  surveyId,
  listQueryParams,
  searchText,
}: SurveySubmissionListTableProps) {
  const lastViewedSubmissionId = useRevealLastViewedRow('surveySubmissions');
  const surveyQuery = useQuery(surveyDetailQueryOptions(surveyId));
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(surveySubmissionsInfiniteQueryOptions(surveyId, listQueryParams));

  const questions = useMemo(
    () => sortBySortOrder(surveyQuery.data?.questions ?? []),
    [surveyQuery.data],
  );
  const columns = useMemo(
    () => getSurveySubmissionColumns(surveyId, questions, searchText),
    [surveyId, questions, searchText],
  );

  const isTableLoading = isLoading || surveyQuery.isLoading;
  const stats: LabeledStat[] = [
    { key: 'questions', label: 'Questions', value: surveyQuery.data?.questions.length },
    { key: 'respondents', label: 'Respondents', value: firstPageMeta?.totals.respondents },
    { key: 'responses', label: 'Responses', value: firstPageMeta?.total },
  ];

  const error = loadError ?? surveyQuery.error;
  if (error) {
    return (
      <ErrorState
        error={error}
        fallbackTitle="Could not load submissions"
        onRetry={() => {
          void refetch();
          void surveyQuery.refetch();
        }}
        fill
      />
    );
  }

  return (
    <>
      <StatCardGrid stats={stats} isLoading={isTableLoading} />

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isTableLoading}
        stickyColumnId={SURVEY_SUBMISSION_NAME_COLUMN_ID}
        emptyIcon={ClipboardList}
        emptyMessage="No submissions found"
        isRowHighlighted={(submission) => submission.id === lastViewedSubmissionId}
        {...loadMoreProps}
        className="lg:flex-1"
      />
    </>
  );
}
